import { IconPlus, IconX } from "@/components/Icons";
import { Button, CheckboxField, Field, Input, Row, Rows, Select } from "@/components/kit";
import type { FieldDef, FieldType } from "@/lib/api/types";
import { toCode } from "@/lib/builder";

/**
 * An ordered list of declared fields.
 *
 * Used by every section, because "a list of things you named, in the order you
 * named them" is the shape of nearly the whole definition. The order is the
 * only layout instruction the generator ever gets — the list here *is* the form
 * an operator will fill in later — so moving a row is a first-class action
 * rather than a convenience.
 */

/** The catalog, in the order an operator is likely to want them. */
const TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "integer", label: "Whole number" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Yes or no" },
  { value: "enum", label: "One of a list" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date and time" },
  { value: "duration", label: "Time taken" },
  { value: "country", label: "Country" },
  { value: "ref", label: "Another record" },
  { value: "ratio", label: "Ratio" },
  { value: "richtext", label: "Long text" },
];

/** "a rule", "an extra rule". A hardcoded "a" produced "Add a extra rule", which
 *  is the kind of thing that makes a screen look unfinished however correct the
 *  rest of it is. Vowel-initial is the whole rule; nothing here begins with a
 *  silent h or a long u. */
function article(word: string): string {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

export function Fields({
  fields,
  onChange,
  masters,
  what = "detail",
}: {
  fields: FieldDef[];
  onChange: (fields: FieldDef[]) => void;
  /** Master codes a `ref` field can point at — its own included, because a
   * horse's sire is a horse. */
  masters: { code: string; label: string }[];
  what?: string;
}) {
  const replace = (i: number, patch: Partial<FieldDef>) =>
    onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const add = () =>
    onChange([...fields, { key: "", label: "", type: "text", required: false }]);

  return (
    <Rows
      empty={`None yet.`}
      onAdd={add}
      addLabel={`Add ${article(what)} ${what}`}
      count={fields.length}
      onMove={(from, to) => {
        const next = [...fields];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onChange(next);
      }}
      onRemove={(i) => onChange(fields.filter((_, j) => j !== i))}
    >
      {fields.map((field, i) => (
        <Row key={i} index={i}>
          <Field label="Name" htmlFor={`f-${i}-label`}>
            <Input
              id={`f-${i}-label`}
              value={field.label ?? ""}
              placeholder="Batting style"
              onChange={(e) =>
                replace(i, {
                  label: e.target.value,
                  // The code follows the name until someone edits it by hand,
                  // so nobody has to think about codes at all in the usual case.
                  key: field.key === toCode(field.label ?? "") ? toCode(e.target.value) : field.key,
                })
              }
            />
          </Field>

          <Field label="Kind" htmlFor={`f-${i}-type`}>
            <Select
              id={`f-${i}-type`}
              value={field.type}
              onChange={(e) => replace(i, { type: e.target.value as FieldType })}
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>

          {field.type === "enum" && (
            <Field label="Choices" hint="Comma separated" htmlFor={`f-${i}-options`}>
              <Input
                id={`f-${i}-options`}
                value={(field.options ?? []).map((o) => o.label ?? o.value).join(", ")}
                placeholder="Right hand, Left hand"
                onChange={(e) =>
                  replace(i, {
                    options: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((label) => ({ value: toCode(label), label })),
                  })
                }
              />
            </Field>
          )}

          {field.type === "ref" && (
            <Field label="Points at" htmlFor={`f-${i}-target`}>
              <Select
                id={`f-${i}-target`}
                value={field.target ?? ""}
                onChange={(e) => replace(i, { target: e.target.value })}
              >
                <option value="">Pick one</option>
                {masters.map((m) => (
                  <option key={m.code} value={`master:${m.code}`}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {(field.type === "integer" || field.type === "number") && (
            <>
              <Field label="Least" htmlFor={`f-${i}-min`}>
                <Input
                  id={`f-${i}-min`}
                  type="number"
                  value={field.min ?? ""}
                  onChange={(e) =>
                    replace(i, {
                      min: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                />
              </Field>
              <Field label="Most" htmlFor={`f-${i}-max`}>
                <Input
                  id={`f-${i}-max`}
                  type="number"
                  value={field.max ?? ""}
                  onChange={(e) =>
                    replace(i, {
                      max: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                />
              </Field>
            </>
          )}

          <CheckboxField
            label="Required"
            checked={field.required ?? false}
            onChange={(e) => replace(i, { required: e.target.checked })}
          />
        </Row>
      ))}
    </Rows>
  );
}

/** A bare list of names — alignments, slot names, role names. The commonest
 * shape after a field list, and not worth a whole editor each time. */
export function Names({
  values,
  onChange,
  placeholder,
  label,
  hint,
  id,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  label: string;
  hint?: string;
  id: string;
}) {
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <Input
        id={id}
        value={values.join(", ")}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
      />
    </Field>
  );
}

/** Add-a-thing button, used wherever a section grows a list. */
export function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button onClick={onClick}>
      <IconPlus size={15} />
      {label}
    </Button>
  );
}

/** Remove-a-thing button. Separate so every list removes the same way. */
export function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button tone="subtle" icon aria-label={label} onClick={onClick}>
      <IconX size={15} />
    </Button>
  );
}
